import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Preloader,
  BottomIcon,
  LocationIcon,
  TopIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { render, unmountComponentAtNode } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';

import { ClubList } from '../ClubList';
import { AuthContext } from '../constants';
import { RELOGIN, USER_CLUBS } from '../schema';
import {
  IReloginRequest,
  IReloginResponse,
  IUserClub,
  IUserClubsResponse,
} from '../types';
import s from './ClubSelect.module.scss';

export const ClubSelect: FC = () => {
  const navigate = useNavigate();
  const {
    data: { tokens },
    actions: { setTokens },
  } = useContext(AuthContext);
  const { t } = useContext(LocalizationContext);
  const { clearFeatures } = useContext(FeaturesContext);

  const [isAllDataFetching, setIsAllDataFetching] = useState(false);

  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLDivElement>(null);

  const [relogin, { client }] = useMutation<IReloginResponse, IReloginRequest>(
    RELOGIN,
    {
      onCompleted: (data) => {
        if (data?.relogin) {
          const { access_token, refresh_token, expires_in } = data.relogin;

          navigate('', { replace: true }); // todo?

          clearFeatures();
          setTokens({ access_token, refresh_token, expires_in });

          setIsAllDataFetching(true);

          client.resetStore().then(() => {
            setIsAllDataFetching(false);
          });

          setIsVisible(false);
        }
      },
    }
  );

  const { data } = useQuery<IUserClubsResponse>(USER_CLUBS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!isVisible) return;

    const handleMouseDown = (e: MouseEvent): void => {
      const target = e.target as Node;

      if (
        menuRef.current?.contains(target) ||
        openerRef.current?.contains(target)
      ) {
        return;
      }

      setIsVisible(false);
    };

    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isVisible]);

  const encodedToken = atob(tokens?.access_token?.split('.')[1] ?? '');
  const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

  const currentClub = data?.userClubs.find((club) => club.id === clubId);

  useEffect(() => {
    if (isAllDataFetching) {
      const modalNode = document.getElementById('modal-root');

      if (!modalNode) return;

      render(
        <div className={s['overlay']}>
          <Preloader loading={true} />
        </div>,
        modalNode
      );

      return () => {
        unmountComponentAtNode(modalNode);
      };
    }
  }, [isAllDataFetching]);

  const clubs = useMemo<IUserClub[] | null>(() => {
    if (!data?.userClubs) return null;

    const filteredClubs = data.userClubs.filter(({ permitted }) => permitted);

    return filteredClubs.length > 1 ? filteredClubs : null;
  }, [data]);

  if (!clubs) return null;

  return (
    <div className={cn(s.container)}>
      <div ref={openerRef}>
        <Button
          className={s['current-club']}
          variant="ghost"
          onClick={() => {
            setIsVisible((prev) => !prev);
          }}
          disabled={(data?.userClubs.length ?? 0) === 1}
        >
          <LocationIcon className={s.icon} />
          <span>{currentClub?.name}</span>
          {(data?.userClubs.length ?? 0) > 1 &&
            (isVisible ? (
              <TopIcon className={s['direction-icon']} />
            ) : (
              <BottomIcon className={s['direction-icon']} />
            ))}
        </Button>
      </div>

      {isVisible && data?.userClubs && (
        <div className={s['clubs']} ref={menuRef}>
          <ClubList
            clubs={clubs}
            currentClubId={clubId}
            selectedClubId={selectedClubId}
            setSelectedClubId={setSelectedClubId}
          />
          <Button
            className={s['submit']}
            disabled={!selectedClubId}
            onClick={() => {
              if (!selectedClubId) return;

              relogin({ variables: { id: selectedClubId } });
            }}
            fullWidth
          >
            {t('clubs.submit')}
          </Button>
        </div>
      )}
    </div>
  );
};
